import type { EditionItem, Group, Set } from '~/types/elements';
import type { CppModel } from '~/types/preview';

const CUTOFF_TYPE = 'cpp-editortoplist-cutoff';
const TOPLIST_PREFIX = 'cpp-editortoplist';
const ARTICLE_TYPES = ['news', 'featureArticle', 'video'];
const EXCLUDED_TEASER_TYPES = ['cpp-article-custom'];

export type ToplistFeedItem = CppModel;

export interface ToplistSplitResult {
  items: EditionItem[];
  feed: ToplistFeedItem[];
  toplists: CppModel[];
}

/**
 * Splits an assembled edition (array of sets/groups) at the
 * `cpp-editortoplist-cutoff` marker. Everything after the marker is
 * collected as the toplist `feed`; the marker and the after-cutoff
 * content are removed from the rendered `items`. Every set + model
 * holding a `cpp-editortoplist-*` placeholder is cloned so the caller
 * can inject the feed into each placeholder without mutating the source
 * payload (which `findCppPlaceholders` re-scans on every collection
 * update). Multiple placeholders share the same feed.
 *
 * Handles both top-level `Set[]` and `Group → Set[]` shapes when
 * locating toplist placeholders. After-cutoff groups are dropped and
 * their articles collected into the feed.
 */
export default function useEditorsToplistCutoff () {
  const { public: { lifespanExpired } } = useRuntimeConfig();
  const isSet = (item: EditionItem): item is Set => (item as Set)?.type === 'set';

  const isToplistPlaceholder = (model: CppModel): boolean => {
    if (model?.type !== 'cpp-placeholder') { return false; }
    const fieldsType = (model.fields?.type as string) || '';
    return fieldsType.split('-').slice(0, 2).join('-') === TOPLIST_PREFIX;
  };

  const collectArticles = (models: CppModel[], feed: ToplistFeedItem[]): void => {
    models.forEach((model) => {
      if (!model?.id || !ARTICLE_TYPES.includes(model?.type)) { return; }
      if (EXCLUDED_TEASER_TYPES.includes(model?.teaser_type as string)) { return; }
      if (model.life_span === String(lifespanExpired)) { return; }
      feed.push(model);
    });
  };

  /*
   * Clone every set/model that holds a toplist placeholder within a
   * flat Set[]; capture all clone references.
   */
  const cloneSetWithToplist = (sets: Set[]): { sets: Set[]; toplists: CppModel[] } => {
    const toplists: CppModel[] = [];
    const cloned = sets.map((set) => {
      const matches: number[] = [];
      set.items.forEach((m, i) => { if (isToplistPlaceholder(m)) { matches.push(i); } });
      if (matches.length === 0) { return set; }
      const models = set.items.slice();
      matches.forEach((index) => {
        const model: CppModel = { ...models[index], fields: { ...(models[index].fields || {}) } };
        models[index] = model;
        toplists.push(model);
      });
      return { ...set, items: models };
    });
    return { sets: cloned, toplists };
  };

  /*
   * Clone every set + model that holds a toplist placeholder; capture
   * all clone references. Also handles Groups.
   */
  const cloneWithToplist = (items: EditionItem[]): { items: EditionItem[]; toplists: CppModel[] } => {
    const toplists: CppModel[] = [];
    const cloned = items.map((item) => {
      if (isSet(item)) {
        const { sets, toplists: found } = cloneSetWithToplist([item]);
        if (found.length > 0) {
          toplists.push(...found);
          return sets[0];
        }
        return item;
      }
      // Handle Group: search within its sets
      const group = item as Group;
      if (Array.isArray(group?.items)) {
        const { sets: clonedSets, toplists: found } = cloneSetWithToplist(group.items);
        if (found.length > 0) {
          toplists.push(...found);
          return { ...group, items: clonedSets };
        }
      }
      return item;
    });
    return { items: cloned, toplists };
  };

  function splitAtCutoff (items: EditionItem[]): ToplistSplitResult {
    if (!Array.isArray(items)) {
      return { items, feed: [], toplists: [] };
    }

    let markerTopIndex = -1;
    let markerGroupSetIndex = -1;
    let markerItemIndex = -1;
    for (let i = 0; i < items.length && markerTopIndex === -1; i++) {
      const item = items[i];
      if (isSet(item)) {
        const idx = item.items.findIndex(model => model?.type === CUTOFF_TYPE);
        if (idx !== -1) {
          markerTopIndex = i;
          markerItemIndex = idx;
        }
      } else if (Array.isArray((item as Group)?.items)) {
        const groupSets = (item as Group).items;
        for (let j = 0; j < groupSets.length && markerTopIndex === -1; j++) {
          const idx = groupSets[j].items.findIndex(model => model?.type === CUTOFF_TYPE);
          if (idx !== -1) {
            markerTopIndex = i;
            markerGroupSetIndex = j;
            markerItemIndex = idx;
          }
        }
      }
    }

    if (markerTopIndex === -1) {
      const { items: cloned, toplists } = cloneWithToplist(items);
      return { items: cloned, feed: [], toplists };
    }

    const feed: ToplistFeedItem[] = [];
    const beforeItems = items.slice(0, markerTopIndex);
    const markerHost = items[markerTopIndex];

    if (markerGroupSetIndex === -1) {
      const markerSet = markerHost as Set;
      collectArticles(markerSet.items.slice(markerItemIndex + 1), feed);
      const trimmedMarkerItems = markerSet.items.slice(0, markerItemIndex);
      if (trimmedMarkerItems.length > 0) {
        beforeItems.push({ ...markerSet, items: trimmedMarkerItems });
      }
    } else {
      const group = markerHost as Group;
      const markerSet = group.items[markerGroupSetIndex];
      collectArticles(markerSet.items.slice(markerItemIndex + 1), feed);
      for (let j = markerGroupSetIndex + 1; j < group.items.length; j++) {
        collectArticles(group.items[j].items, feed);
      }
      const trimmedSets = group.items.slice(0, markerGroupSetIndex);
      const trimmedMarkerItems = markerSet.items.slice(0, markerItemIndex);
      if (trimmedMarkerItems.length > 0) {
        trimmedSets.push({ ...markerSet, items: trimmedMarkerItems });
      }
      if (trimmedSets.length > 0) {
        beforeItems.push({ ...group, items: trimmedSets });
      }
    }

    for (let i = markerTopIndex + 1; i < items.length; i++) {
      const item = items[i];
      if (isSet(item)) {
        collectArticles(item.items, feed);
      } else if (Array.isArray((item as Group)?.items)) {
        (item as Group).items.forEach(set => collectArticles(set.items, feed));
      }
    }

    const { items: cloned, toplists } = cloneWithToplist(beforeItems);
    return { items: cloned, feed, toplists };
  }

  return { splitAtCutoff };
}
